import { EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Input, Select, Table, Tag } from "antd";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAllSubscriptions } from "../actions/subscription_actions";
import ContentLayout from "../components/layout/ContentLayout";
import SubscriptionDetailsModal from "../components/SubscriptionDetailsModal";

const statusColors = {
  active: "green",
  inactive: "gold",
  past_due: "red",
  cancelling: "orange",
  cancelled: "default",
  overdue: "red",
};

const statusOptions = [
  { value: "", label: "All statuses" },
  { value: "PENDING_ACTIVATION", label: "Pending activation" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAST_DUE", label: "Past due" },
  { value: "CANCELLING", label: "Cancelling" },
  { value: "CANCELED", label: "Cancelled" },
  { value: "UNPAID", label: "Unpaid" },
];

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const formatMoney = (amount, currency = "USD") => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).format(Number(amount));
};

export default function Subscriptions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || searchParams.get("accountUrn") || "";
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["ops-subscriptions", page, limit, search, status],
    queryFn: () =>
      getAllSubscriptions({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      }),
    keepPreviousData: true,
    retry: false,
  });

  const subscriptions = data?.docs || [];
  const pagination = data?.pagination || {};

  const submitSearch = (value) => {
    const nextSearch = String(value || "").trim();
    setSearch(nextSearch);
    setSearchInput(nextSearch);
    setPage(1);
    if (nextSearch) setSearchParams({ search: nextSearch });
    else setSearchParams({});
  };

  const columns = [
    {
      title: "Account",
      key: "account",
      width: 250,
      render: (_, row) => (
        <div>
          <strong>{row.account_name || row.account_slug || "Unknown account"}</strong>
          <div className="mono-cell subscription-secondary">
            {row.account_urn || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Businesses",
      key: "businesses",
      width: 220,
      render: (_, row) => (
        <div>
          <strong>{row.businesses?.length || 0} linked</strong>
          <div className="subscription-secondary">
            {row.businesses?.map((business) => business.name).join(", ") || "No businesses linked"}
          </div>
        </div>
      ),
    },
    {
      title: "Plan",
      dataIndex: "plan_name",
      width: 150,
      render: (value, row) => value || row.name || "-",
    },
    {
      title: "Period",
      dataIndex: "period",
      width: 110,
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 135,
      render: (value) => (
        <Tag color={statusColors[value] || "default"}>
          {(value || "unknown").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Recurring",
      width: 130,
      render: (_, row) => (
        <div>
          {formatMoney(row.amount, row.currency)}
          {row.discount && (
            <div>
              <Tag color={row.discount.status === "ACTIVE" ? "magenta" : "default"}>
                {row.discount.code} −{Number(row.discount.percent_off)}%
              </Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Sites",
      dataIndex: "sites_count",
      width: 80,
      align: "center",
    },
    {
      title: "Next billing",
      dataIndex: "next_billing_date",
      width: 140,
      render: formatDate,
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 105,
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedSubscription(row);
          }}
        >
          Manage
        </Button>
      ),
    },
  ];

  return (
    <ContentLayout
      header={
        <h1>
          Subscriptions <span>Operations</span>
        </h1>
      }
      bodyClassName="subscriptions-page"
    >
      <div className="subscription-toolbar">
        <Input.Search
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onSearch={submitSearch}
          placeholder="Search account, account URN, business name, slug, or billing email"
          enterButton={<SearchOutlined />}
          allowClear
        />
        <Select
          value={status}
          options={statusOptions}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
      </div>

      {isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load subscriptions"
          description={error?.response?.data?.message || error?.message}
        />
      )}

      <Table
        columns={columns}
        dataSource={subscriptions}
        loading={isLoading}
        rowKey="id"
        onRow={(row) => ({
          onClick: () => setSelectedSubscription(row),
          className: "subscription-row",
        })}
        pagination={{
          current: pagination.page || page,
          pageSize: pagination.limit || limit,
          total: pagination.totalItems || 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total) => `${total} subscriptions`,
          onChange: (nextPage, nextLimit) => {
            setPage(nextLimit !== limit ? 1 : nextPage);
            setLimit(nextLimit);
          },
        }}
        scroll={{ x: 1420 }}
      />

      <SubscriptionDetailsModal
        open={!!selectedSubscription}
        onClose={() => setSelectedSubscription(null)}
        subscription={selectedSubscription}
        accountUrn={selectedSubscription?.account_urn}
        accountName={selectedSubscription?.account_name}
        onUpdated={refetch}
      />
    </ContentLayout>
  );
}
